import { TIP_ACTION_TYPES } from './types';
import tipService from '../services/tipService';

/**
 * Cancel/delete a tip action (without dispatch)
 * @param {string} tipId - Tip ID to cancel
 * @param {boolean} isManualRemove - Whether to manually remove the tip (true) or cancel the tip (false)  
 * @returns {Promise<Object>} Cancel tip result
 */
export const cancelTip = async (tipId, isManualRemove = false) => {
  try {
    if (!tipId) {
      return { success: false, error: 'Tip ID is required' };
    }

    const response = await tipService.cancelTip(tipId, isManualRemove);

    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message || 'Failed to cancel tip' };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while cancelling the tip';
    return { success: false, error: errorMessage };
  }
};

/**
 * Create a new tip action (without dispatch)
 * @param {string} id - Content or message ID
 * @param {Object} tipData - Tip data containing tip_type, amount, returnUrl
 * @param {boolean} isMessagesTip - Whether this is a message tip (true) or content tip (false)
 * @returns {Promise<Object>} Create tip result
 */
export const sendTip = async (id, tipData, isMessagesTip = false) => {
  try {
    if (!id) {
      return { success: false, error: 'Content or message ID is required' };
    }

    if (!tipData || !tipData.amount) {
      return { success: false, error: 'Tip amount is required' };
    }

    const response = await tipService.sendTip(id, tipData, isMessagesTip);

    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message || 'Failed to create tip' };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while creating the tip';
    return { success: false, error: errorMessage };
  }
};

/**
 * Extend the reply deadline for a message tip (without dispatch)
 * @param {string} tipId - Tip ID whose deadline should be extended
 * @returns {Promise<Object>} Extend deadline result
 */
export const extendTipDeadline = async (tipId) => {
  try {
    if (!tipId) {
      return { success: false, error: 'Tip ID is required' };
    }

    const response = await tipService.extendTipDeadline(tipId);

    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message || 'Failed to extend tip deadline' };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while extending the tip deadline';
    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch creator message/tips earnings (optional per-subscriber breakdown).
 * @param {string} [subscriberId] - Subscriber Mongo user id when scoped to one chat partner
 * @returns {Function} Redux thunk — dispatches request/success/failure; resolves like other async actions
 */
export const fetchMessageTipsEarnings = (subscriberId) => async (dispatch) => {
  dispatch({ type: TIP_ACTION_TYPES.FETCH_MESSAGE_TIPS_EARNINGS_REQUEST });

  try {
    const res = await tipService.getMessageTipsEarnings(subscriberId);

    if (res.success) {
      dispatch({
        type: TIP_ACTION_TYPES.FETCH_MESSAGE_TIPS_EARNINGS_SUCCESS,
        payload: { data: res.data, subscriberId: subscriberId ?? null },
      });
      return { success: true, data: res.data, message: res.message };
    }

    const errorMessage = res.message || 'Failed to fetch message tips earnings';
    dispatch({
      type: TIP_ACTION_TYPES.FETCH_MESSAGE_TIPS_EARNINGS_FAILURE,
      payload: errorMessage,
    });
    return {
      success: false,
      error: errorMessage,
      status: res.status,
    };
  } catch (error) {
    const errorMessage =
      error?.message || 'Failed to fetch message tips earnings';
    dispatch({
      type: TIP_ACTION_TYPES.FETCH_MESSAGE_TIPS_EARNINGS_FAILURE,
      payload: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch pending tip amount awaiting creator collection (without dispatch).
 * @returns {Promise<Object>} Pending collect result
 */
export const getPendingCollectTips = async () => {
  try {
    const response = await tipService.getPendingCollectTips();

    if (response.success) {
      return { success: true, data: response.data };
    }

    return { success: false, error: response.message || 'Failed to fetch pending collect amount' };
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'An unexpected error occurred while fetching pending collect amount';
    return { success: false, error: errorMessage };
  }
};

/**
 * Clear tip-related Redux errors (e.g. message tips earnings fetch error)
 */
export const clearTipErrors = () => (dispatch) => {
  dispatch({ type: TIP_ACTION_TYPES.CLEAR_TIP_ERRORS });
};


